import React from 'react';
import styled, { keyframes } from 'styled-components';

import Button from '../../../Button';

import { ArrowEnter as ArrowEnterIcon } from '@icons/communication';
import { Close as DeleteIcon } from '@icons/common';
import { Attachment as AttachmentIcon } from '@icons/editor';
import { AttachmentType } from '@components/CRM/Chains/Message2/Message2';
import { uniqBy } from 'lodash';
import AttachmentList2 from '@components/CRM/Chains/AttachmentList2/AttachmentList2';
import ActionPopup from '@components/CRM/Common/ActionPopup';

interface NoteFormProps {
  className?: string;
  value: string;
  placeholder?: string;
  isAllowedAttachmentsUploading: boolean;
  onSubmit: (params: {
    value: NoteFormProps['value'];
    attachments: File[];
    onSuccess?: () => void;
    onError?: () => void;
  }) => void;
  onCancel: () => void;
}

interface NoteFormState {
  value: NoteFormProps['value'];
  initialValue: NoteFormProps['value'];
  attachments: File[];
  attachmentsError: boolean;
  isLoading: boolean;
}

class NoteForm extends React.PureComponent<NoteFormProps, NoteFormState> {
  static defaultProps = {
    className: '',
    value: '',
    placeholder: '',
    isAllowedAttachmentsUploading: true,
  };

  private textarea: React.RefObject<HTMLTextAreaElement>;
  private uploaderRef: React.RefObject<HTMLInputElement>;

  private minTextareaHeight: string;

  static getDerivedStateFromProps(
    nextProps: Readonly<NoteFormProps & { value: string }>,
    prevState: NoteFormState
  ) {
    const { value } = nextProps;
    const { initialValue } = prevState;

    if (value === initialValue) return null;

    return {
      initialValue: value,
      value,
    };
  }

  constructor(props) {
    super(props);

    this.state = {
      value: props.value,
      initialValue: props.value,
      attachments: [],
      attachmentsError: false,
      isLoading: false,
    };

    this.textarea = React.createRef();
    this.uploaderRef = React.createRef();
    this.minTextareaHeight = '114px';
    this.resizeTextarea = this.resizeTextarea.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handlePaste = this.handlePaste.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleUploading = this.handleUploading.bind(this);
    this.triggerUploader = this.triggerUploader.bind(this);
    this.handleDeleteAttachment = this.handleDeleteAttachment.bind(this);
    this.toggleIsAttachmentSizeError =
      this.toggleIsAttachmentSizeError.bind(this);
    this.handleSubmitAttachmentError =
      this.handleSubmitAttachmentError.bind(this);
  }

  componentDidMount() {
    const { initialValue } = this.state;

    if (this.textarea?.current) {
      this.minTextareaHeight = window
        .getComputedStyle(this.textarea.current)
        .getPropertyValue('min-height');
    }

    if (initialValue.length && this.textarea.current) {
      this.textarea.current.style.height = this.minTextareaHeight;
      this.resizeTextarea();
    }
  }

  handleChange({ target }) {
    this.setState({ value: target.value }, this.resizeTextarea);
  }

  handleSubmit() {
    const { onSubmit } = this.props;
    const { value, attachments } = this.state;

    this.setState({ isLoading: true });

    const onSuccess = () => {
      this.setState({ isLoading: false });
    };

    const onError = () => {
      this.setState({ isLoading: false });
    };
    onSubmit({ value, attachments, onSuccess, onError });
  }

  handleCancel() {
    const { initialValue } = this.state;
    const { onCancel } = this.props;
    const updatedState = { value: initialValue };

    this.setState(updatedState, onCancel);

    if (this.textarea.current) {
      this.textarea.current.value = initialValue;
      this.textarea.current.style.height = this.minTextareaHeight;
      this.resizeTextarea();
    }
  }

  handleKeyDown({ key, shiftKey }) {
    if (key === 'Backspace' && this.textarea.current) {
      this.textarea.current.style.height = this.minTextareaHeight;
    }

    if (!shiftKey && key === 'Enter') {
      this.handleSubmit();
    }
  }

  handlePaste(e) {
    const items = e.clipboardData.items;

    for (let item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();

        if (file) {
          let isSizeError = false;
          if (file.size > 10 ** 7 / 2) {
            isSizeError = true;
          }

          if (!isSizeError) {
            this.setState(({ attachments: prevAttachments }) => ({
              attachments: uniqBy(
                [...prevAttachments, file],
                attach => attach.name
              ),
            }));
          } else {
            this.setState({ attachmentsError: true });
          }
        }
      }
    }
  }

  resizeTextarea() {
    const el = this.textarea.current;
    if (el) {
      const { scrollHeight } = el;
      el.style.height = `${scrollHeight}px`;
    }
  }

  triggerUploader() {
    if (this.uploaderRef.current) this.uploaderRef.current.click();
  }

  handleUploading(event: React.ChangeEvent<HTMLInputElement>) {
    event.persist();
    event.preventDefault();
    event.stopPropagation();

    const files = event.target.files;
    if (!files) return;

    let isSizeError = false;
    Array.from(files).forEach(file => {
      if (file.size > 10 ** 7 / 2) {
        isSizeError = true;
      }
    });

    if (!isSizeError) {
      this.setState(({ attachments: prevAttachments }) => ({
        attachments: uniqBy([...prevAttachments, ...files], file => file.name),
      }));
    } else {
      this.setState({ attachmentsError: true });
    }
  }

  handleDeleteAttachment({
    name,
    lastModified,
  }: {
    name?: string;
    lastModified?: number;
  }) {
    if (!name || !lastModified) return;

    this.setState(({ attachments: prevAttachments }) => ({
      attachments: prevAttachments.filter(
        attachment =>
          attachment.lastModified !== lastModified || attachment.name !== name
      ),
    }));
  }

  toggleIsAttachmentSizeError() {
    this.setState(({ attachmentsError: prev }) => ({
      attachmentsError: !prev,
    }));
  }

  handleSubmitAttachmentError() {
    this.triggerUploader();
    this.setState({ attachmentsError: false });
  }

  render() {
    const { value, attachments, attachmentsError, isLoading } = this.state;
    const { className, placeholder, isAllowedAttachmentsUploading } =
      this.props;
    return (
      <StyledNoteForm className={className}>
        {isLoading && <StyledSkeleton />}
        <StyledFormWrapper>
          <StyledTextarea
            autoFocus
            value={value}
            ref={this.textarea}
            placeholder={placeholder}
            onChange={this.handleChange}
            onKeyDown={this.handleKeyDown}
            onPaste={this.handlePaste}
          />
          <StyledActions>
            <StyledCancel functional onClick={this.handleCancel}>
              <StyledDeleteIcon />
            </StyledCancel>
            {isAllowedAttachmentsUploading && (
              <>
                <StyledAttachButton functional onClick={this.triggerUploader}>
                  <StyledAttachmentIcon />
                  <input
                    multiple
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    ref={this.uploaderRef}
                    onChange={this.handleUploading}
                  />
                </StyledAttachButton>
                <ActionPopup
                  title="Невозможно прикрепить файлы"
                  description="Объем каждого файла не может превышать 5 МБ."
                  submitText="Прикрепить другой"
                  isOpen={attachmentsError}
                  onCancel={this.toggleIsAttachmentSizeError}
                  onSubmit={this.handleSubmitAttachmentError}
                />
              </>
            )}
            <StyledButton
              functional
              disabled={!value.length && !attachments.length}
              onClick={this.handleSubmit}
            >
              <StyledArrowEnterIcon />
            </StyledButton>
          </StyledActions>
        </StyledFormWrapper>
        {attachments.length > 0 && (
          <StyledAttachmentList
            onDelete={this.handleDeleteAttachment}
            list={attachments}
            isWithPreview
          />
        )}
      </StyledNoteForm>
    );
  }
}

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

export const StyledSkeleton = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 4px;
  opacity: 0.75;
  z-index: 1;
  background: linear-gradient(90deg, #eeeeee 25%, #dddddd 37%, #eeeeee 63%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease infinite;
`;

const StyledAttachmentList = styled(AttachmentList2)`
  margin-top: 10px;
  a,
  span {
    color: #4d4d4d;
    font-size: 10px;
  }
  svg > path {
    stroke: #4d4d4d;
  }
`;

const StyledFormWrapper = styled.div`
  display: flex;
`;

const StyledActions = styled.div`
  display: flex;
  width: 14px;
  margin-left: 10px;
  align-items: center;
  flex-direction: column;
`;

const StyledNoteForm = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
`;

export const StyledTextarea = styled.textarea`
  width: 100%;
  resize: none;
  height: 114px;
  font-size: 14px;
  padding: 4px 8px;
  font-family: 'Backpack';
  background: none;
  min-height: 114px;
  max-height: 114px;
  border-radius: 4px;
  border: solid 1px #f5efa8;
`;

const StyledButton = styled(Button)`
  path {
    stroke: #919499;
  }

  &:hover {
    path {
      stroke: #007aff;
    }
  }

  ${props =>
    props.disabled &&
    `
    opacity: .7;
    pointer-events: none;
  `}
`;

const StyledArrowEnterIcon = styled(ArrowEnterIcon)`
  width: 14px;
  height: 11px;
`;

export const StyledCancel = styled(StyledButton)`
  margin-bottom: 10px;
`;

const StyledAttachButton = styled(StyledButton)`
  margin-bottom: 10px;

  & > input {
    display: none;
  }
`;

const StyledAttachmentIcon = styled(AttachmentIcon)`
  width: 12px;
  height: 12px;
`;

const StyledDeleteIcon = styled(DeleteIcon)`
  width: 12px;
  height: 12px;
`;

export default NoteForm;
